/* Admin dashboard. One request (GET /api/dashboard, owner-scoped) feeds the live matches, the stat row
 * and the pending queue; refreshed every 15 s while the tab is visible. Super admins also get a compact
 * System health card (GET /api/metrics + /api/status, every 5 s while visible). */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { parseServerDate } from '../utils/timeUtils';
import './dashboard/dashboard.css';

const DATA_MS = 15000;
const HEALTH_MS = 5000;

/** setInterval that only runs while the browser tab is visible (and fires once on becoming visible). */
function useVisibleInterval(fn, ms, enabled = true) {
  useEffect(() => {
    if (!enabled) return undefined;
    let id = null;
    const start = () => { if (!id) id = setInterval(fn, ms); };
    const stop = () => { clearInterval(id); id = null; };
    const onVis = () => { if (document.hidden) stop(); else { fn(); start(); } };
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVis);
    return () => { stop(); document.removeEventListener('visibilitychange', onVis); };
  }, [fn, ms, enabled]);
}

function fmtBytes(b) {
  const n = Number(b);
  if (!Number.isFinite(n) || n < 0) return '-';
  return n >= 1073741824 ? `${(n / 1073741824).toFixed(1)} GB` : `${Math.round(n / 1048576)} MB`;
}
function fmtUptime(s) {
  const x = Math.max(0, Math.floor(Number(s) || 0));
  const d = Math.floor(x / 86400), h = Math.floor((x % 86400) / 3600), m = Math.floor((x % 3600) / 60);
  return d ? `${d}d ${h}h` : h ? `${h}h ${m}m` : `${m}m`;
}
function relTime(value, lng) {
  const d = parseServerDate(value);
  if (!d) return '';
  const sec = Math.round((d.getTime() - Date.now()) / 1000);
  const abs = Math.abs(sec);
  const [v, unit] = abs < 60 ? [sec, 'second'] : abs < 3600 ? [Math.round(sec / 60), 'minute'] : abs < 86400 ? [Math.round(sec / 3600), 'hour'] : [Math.round(sec / 86400), 'day'];
  try { return new Intl.RelativeTimeFormat(lng || undefined, { numeric: 'auto' }).format(v, unit); } catch (_) { return ''; }
}
function mapName(m) {
  let list = [];
  try { list = typeof m.map_list === 'string' ? JSON.parse(m.map_list) : (m.map_list || []); } catch (_) { }
  const raw = (Array.isArray(list) && list[m.current_map_index || 0]) || m.map || '';
  return String(raw).replace(/^(de|cs)_/i, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const Ic = ({ d, size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{d}</svg>
);
const ICONS = {
  plus: <><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>,
  teams: <><circle cx="9" cy="8" r="3.5" /><path d="M2 20a7 7 0 0 1 14 0" /><path d="M16 4a3.5 3.5 0 0 1 0 7" /><path d="M18 14a7 7 0 0 1 4 6" /></>,
  servers: <><rect x="3" y="4" width="18" height="7" rx="2" /><rect x="3" y="13" width="18" height="7" rx="2" /><path d="M7 7.5h.01M7 16.5h.01" /></>,
  maps: <><path d="M9 4L3 6v14l6-2 6 2 6-2V4l-6 2z" /><path d="M9 4v14M15 6v14" /></>,
  seasons: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 10h18" /></>,
};

function Flag({ code }) {
  if (!code) return null;
  return <img className="db-flag" src={`https://flagcdn.com/16x12/${String(code).toLowerCase()}.png`} alt={code} onError={e => { e.target.style.display = 'none'; }} />;
}

function stageOf(m, t) {
  if (m.is_paused) return { key: 'paused', label: t('dashboardPage.paused', 'Paused') };
  if (m.match_stage === 'knife') return { key: 'knife', label: t('dashboardPage.knife', 'Knife') };
  if (m.match_stage === 'live') return { key: 'live', label: t('dashboardPage.live', 'Live') };
  return { key: 'warmup', label: t('dashboardPage.warmup', 'Warmup') };
}

function LiveCard({ m }) {
  const { t } = useTranslation();
  const st = stageOf(m, t);
  const bo = parseInt(m.best_of, 10) || 1;
  return (
    <Link to={`/admin/matches/${m.id}`} className="db-live">
      <div className="db-live-top">
        <span className={`db-stage ${st.key}`}>{st.label}</span>
        <span className="db-live-map">
          <span>{mapName(m) || t('dashboardPage.noMap', 'No map')}</span>
          {bo > 1 && <span>{t('dashboardPage.mapOf', 'map {{n}}/{{bo}}', { n: (m.current_map_index || 0) + 1, bo })}</span>}
        </span>
      </div>
      <div className="db-live-score">
        <span className="db-team l" title={m.team1_name}>{m.team1_name}</span>
        <span className="db-score"><b>{m.score_team1 ?? 0}</b><i>:</i><b>{m.score_team2 ?? 0}</b></span>
        <span className="db-team r" title={m.team2_name}>{m.team2_name}</span>
      </div>
      {bo > 1 && <div className="db-series">{t('dashboardPage.series', 'Series')} {m.map_wins_team1 || 0} – {m.map_wins_team2 || 0}</div>}
      <div className="db-live-foot">
        <span className="db-server"><Flag code={m.server_country_code} />{m.server_name || t('dashboardPage.noServer', 'No server')}</span>
        {m.season_name && <span className="db-season">{m.season_name}</span>}
      </div>
    </Link>
  );
}

/** Finished match: BO1 = map score, BO3/BO5 = series (maps won). Winner from winner_team_id, else the
 *  higher score (winner_team_id is often NULL on finished matches). null = draw / unknown. */
function resultOf(m) {
  const bo = parseInt(m.best_of, 10) || 1;
  const a = bo > 1 ? (parseInt(m.map_wins_team1, 10) || 0) : (parseInt(m.score_team1, 10) || 0);
  const b = bo > 1 ? (parseInt(m.map_wins_team2, 10) || 0) : (parseInt(m.score_team2, 10) || 0);
  let winner = null;
  if (m.winner_team_id != null && m.team1_id != null && Number(m.winner_team_id) === Number(m.team1_id)) winner = 1;
  else if (m.winner_team_id != null && m.team2_id != null && Number(m.winner_team_id) === Number(m.team2_id)) winner = 2;
  else if (a !== b) winner = a > b ? 1 : 2;
  return { a, b, bo, winner };
}

function RecentRow({ m, lng }) {
  const { t } = useTranslation();
  const r = resultOf(m);
  return (
    <Link to={`/admin/matches/${m.id}`} className="db-res">
      <span className={`db-res-team l${r.winner === 1 ? ' win' : r.winner ? ' lose' : ''}`} title={m.team1_name}>{m.team1_name}</span>
      <span className="db-res-score"><b className={r.winner === 1 ? 'win' : ''}>{r.a}</b><i>:</i><b className={r.winner === 2 ? 'win' : ''}>{r.b}</b></span>
      <span className={`db-res-team r${r.winner === 2 ? ' win' : r.winner ? ' lose' : ''}`} title={m.team2_name}>{m.team2_name}</span>
      <span className="db-res-meta">
        {r.bo > 1 ? `BO${r.bo}` : (mapName(m) || 'BO1')}
        {!r.winner && <em>{t('dashboardPage.draw', 'Draw')}</em>}
      </span>
      <span className="db-row-time" title={m.end_time || m.created_at}>{relTime(m.end_time || m.created_at, lng)}</span>
    </Link>
  );
}

function Bar({ pct, warn = 75, bad = 90 }) {
  const p = Math.max(0, Math.min(100, Number(pct) || 0));
  return <span className={`db-bar${p >= bad ? ' bad' : p >= warn ? ' warn' : ''}`}><span style={{ transform: `scaleX(${p / 100})` }} /></span>;
}

function HealthCard() {
  const { t } = useTranslation();
  const [m, setM] = useState(null);
  const [svc, setSvc] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(async () => {
    try { const r = await axios.get('/api/metrics'); setM(r.data); } catch (_) { }
    try { const r = await axios.get('/api/status'); setSvc(r.data); } catch (_) { }
  }, []);
  useEffect(() => { load(); }, [load]);
  useVisibleInterval(load, HEALTH_MS);

  const ws = svc ? (svc.websocket?.adminClients || 0) + (svc.websocket?.publicClients || 0) : 0;
  const services = svc ? [
    { label: 'WebSocket', ok: !!svc.websocket?.ok, detail: t('dashboardPage.clients', 'clients: {{count}}', { count: ws }) },
    { label: 'Redis', ok: !!svc.redis?.ok },
    { label: t('dashboardPage.logReceiver', 'Log receiver'), ok: !!svc.logReceiver?.ok, detail: t('dashboardPage.serversN', 'servers: {{count}}', { count: svc.logReceiver?.servers || 0 }) },
  ] : [];
  const details = m ? [
    [t('dashboardPage.load', 'Load 1/5/15 min'), [m.cpu?.loadAverage?.one, m.cpu?.loadAverage?.five, m.cpu?.loadAverage?.fifteen].map(x => (Number(x) || 0).toFixed(2)).join(' / ')],
    [t('dashboardPage.cores', 'Cores'), `${m.cpu?.cores || 0} (${m.cpu?.model || '-'})`],
    [t('dashboardPage.processRam', 'Panel process RAM'), fmtBytes(m.process?.rssBytes)],
    [t('dashboardPage.osUptime', 'Host uptime'), fmtUptime(m.system?.uptime)],
    ['Node', m.process?.nodeVersion || '-'],
    [t('dashboardPage.host', 'Host'), `${m.system?.hostname || '-'} (${[m.system?.platform, m.system?.arch].filter(Boolean).join(' ')})`],
  ] : [];

  return (
    <section className="db-card">
      <div className="db-card-head">
        <h2>{t('dashboard.systemHealth', 'System Health')}</h2>
        {m && <button type="button" className="db-link-btn" onClick={() => setOpen(o => !o)}>{open ? t('dashboardPage.hideDetails', 'Hide details') : t('dashboardPage.details', 'Details')}</button>}
      </div>
      {!m ? <div className="db-empty">{t('common.loading', 'Loading...')}</div> : (<>
        <dl className="db-services">
          {services.map(s => (
            <div key={s.label} className="db-svc">
              <dt>{s.label}{s.detail && <em>{s.detail}</em>}</dt>
              <dd className={s.ok ? 'ok' : 'bad'}>{s.ok ? t('dashboardPage.up', 'Up') : t('dashboardPage.down', 'Down')}</dd>
            </div>
          ))}
        </dl>
        <div className="db-gauges">
          <div className="db-gauge"><span>CPU</span><Bar pct={m.cpu?.percent} /><b>{Math.round(m.cpu?.percent || 0)}%</b></div>
          <div className="db-gauge"><span>RAM</span><Bar pct={m.memory?.percent} /><b>{fmtBytes(m.memory?.usedBytes)} / {fmtBytes(m.memory?.totalBytes)}</b></div>
          {m.disk && (
            <div className="db-gauge" title={t('dashboardPage.storageHint', 'Disk of the panel data folder: database, demos and backups. {{free}} free.', { free: fmtBytes(m.disk.freeBytes) })}>
              <span>{t('dashboardPage.storage', 'Storage')}</span><Bar pct={m.disk.percent} /><b>{fmtBytes(m.disk.usedBytes)} / {fmtBytes(m.disk.usedBytes + m.disk.freeBytes)}</b>
            </div>
          )}
        </div>
        <div className="db-health-meta">
          <span>{t('dashboardPage.uptime', 'Uptime')} <b>{fmtUptime(m.process?.uptime ?? m.uptime)}</b></span>
          <span>{t('dashboardPage.build', 'Build')} <b>{m.process?.buildId || '-'}</b></span>
        </div>
        {open && (
          <dl className="db-details">
            {details.map(([k, v]) => <React.Fragment key={k}><dt>{k}</dt><dd title={v}>{v}</dd></React.Fragment>)}
          </dl>
        )}
      </>)}
    </section>
  );
}

function Dashboard() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await axios.get('/api/dashboard');
      setData(r.data); setError('');
    } catch (e) {
      setError(e.response?.data?.error || t('dashboardPage.loadFailed', 'Could not load the dashboard.'));
    }
  }, [t]);
  useEffect(() => { load(); }, [load]);
  useVisibleInterval(load, DATA_MS);

  const c = data?.counts || {};
  const sv = data?.servers || {};
  const stats = [
    { key: 'live', label: t('dashboardPage.statLive', 'Live'), value: c.live ?? 0, to: '/admin/matches', tone: 'red' },
    { key: 'pending', label: t('dashboard.pending', 'Pending'), value: c.pending ?? 0, to: '/admin/matches', tone: 'amber' },
    { key: 'finished', label: t('dashboardPage.statFinished', 'Finished'), value: c.finished ?? 0, to: '/admin/matches', tone: 'muted' },
    { key: 'servers', label: t('dashboardPage.statServers', 'Servers free'), value: `${sv.available ?? 0}/${sv.total ?? 0}`, sub: sv.players ? t('dashboardPage.players', 'Players online: {{count}}', { count: sv.players }) : null, to: '/admin/servers', tone: 'green' },
    { key: 'teams', label: t('dashboard.totalTeams', 'Total Teams'), value: data?.teams ?? 0, to: '/admin/teams', tone: 'blue' },
  ];
  const actions = [
    { to: '/admin/matches/new', label: t('dashboard.newMatch', 'New Match'), icon: 'plus', primary: true },
    { to: '/admin/teams', label: t('nav.teams', 'Teams'), icon: 'teams' },
    { to: '/admin/servers', label: t('nav.servers', 'Servers'), icon: 'servers' },
    { to: '/admin/maps', label: t('nav.maps', 'Maps'), icon: 'maps' },
    { to: '/admin/seasons', label: t('nav.seasons', 'Seasons'), icon: 'seasons' },
  ];
  const live = data?.live || [];
  const upcoming = data?.upcoming || [];
  const recent = data?.recent || [];

  return (
    <div className="db-page">
      <div className="db-head">
        <div>
          <h1 className="db-title">{t('nav.dashboard', 'Dashboard')}</h1>
          <p className="db-subtitle">{user?.username ? t('dashboardPage.welcome', 'Welcome back, {{name}}', { name: user.username }) : ''}</p>
        </div>
        <Link to="/admin/matches/new" className="db-btn primary"><Ic d={ICONS.plus} size={14} /> {t('dashboard.newMatch', 'New Match')}</Link>
      </div>

      {error && <div className="db-error">{error}</div>}

      {/* Live now */}
      <section>
        <div className="db-section-head">
          <h2>{t('dashboardPage.liveNow', 'Live now')} <span className={`db-count${live.length ? ' live' : ''}`}>{c.live ?? 0}</span></h2>
          {(c.live || 0) > live.length && <Link to="/admin/matches" className="db-link">{t('dashboard.viewAll', 'View All')}</Link>}
        </div>
        {!data ? (
          <div className="db-live-grid">{[0, 1].map(i => <div key={i} className="db-live skel" />)}</div>
        ) : live.length === 0 ? (
          <div className="db-empty-live">
            {t('dashboardPage.noLive', 'No live matches right now.')}{' '}
            {(c.pending || 0) > 0 && t('dashboardPage.pendingHint', 'Waiting to start: {{count}} (see below).', { count: c.pending })}
          </div>
        ) : (
          <div className="db-live-grid">{live.map(m => <LiveCard key={m.id} m={m} />)}</div>
        )}
      </section>

      {/* Stat row */}
      <div className="db-stats">
        {stats.map(s => (
          <Link key={s.key} to={s.to} className={`db-stat${s.value ? ` ${s.tone}` : ''}`}>
            <span className="db-stat-label">{s.label}</span>
            <span className="db-stat-value">{data ? s.value : '–'}</span>
            {s.sub && <span className="db-stat-sub">{s.sub}</span>}
          </Link>
        ))}
      </div>

      <div className="db-grid">
        <div className={isSuperAdmin ? 'db-col' : 'db-col-pair'}>
        {/* Upcoming */}
        <section className="db-card">
          <div className="db-card-head">
            <h2>{t('dashboardPage.upcoming', 'Upcoming matches')} <span className="db-count">{c.pending ?? 0}</span></h2>
            <Link to="/admin/matches" className="db-link">{t('dashboard.viewAll', 'View All')}</Link>
          </div>
          {!data ? <div className="db-empty">{t('common.loading', 'Loading...')}</div>
            : upcoming.length === 0 ? <div className="db-empty">{t('dashboardPage.noUpcoming', 'No matches waiting to start.')}</div>
            : (
              <div className="db-list">
                {upcoming.map(m => (
                  <Link key={m.id} to={`/admin/matches/${m.id}`} className="db-row">
                    <span className="db-row-teams"><b>{m.team1_name}</b><i>vs</i><b>{m.team2_name}</b></span>
                    <span className="db-row-meta">
                      <span className="db-row-map"><span>BO{parseInt(m.best_of, 10) || 1}</span>{mapName(m) && <span>{mapName(m)}</span>}</span>
                      <span className="db-server"><Flag code={m.server_country_code} />{m.server_name || t('dashboardPage.noServer', 'No server')}</span>
                    </span>
                    <span className="db-row-time" title={m.created_at}>{relTime(m.created_at, i18n.language)}</span>
                  </Link>
                ))}
              </div>
            )}
        </section>

        {/* Recent results */}
        <section className="db-card">
          <div className="db-card-head">
            <h2>{t('dashboardPage.recent', 'Recent results')} <span className="db-count">{c.finished ?? 0}</span></h2>
            <Link to="/admin/matches" className="db-link">{t('dashboard.viewAll', 'View All')}</Link>
          </div>
          {!data ? <div className="db-empty">{t('common.loading', 'Loading...')}</div>
            : recent.length === 0 ? <div className="db-empty">{t('dashboardPage.noRecent', 'No finished matches yet.')}</div>
            : <div className="db-list">{recent.map(m => <RecentRow key={m.id} m={m} lng={i18n.language} />)}</div>}
        </section>
        </div>

        {isSuperAdmin && <HealthCard />}
      </div>

      {/* Quick actions */}
      <section>
        <div className="db-section-head"><h2>{t('dashboard.quickActions', 'Quick Actions')}</h2></div>
        <div className="db-actions">
          {actions.map(a => (
            <Link key={a.to} to={a.to} className={`db-action${a.primary ? ' primary' : ''}`}>
              <Ic d={ICONS[a.icon]} />{a.label}
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

export default Dashboard;
